import React from 'react';
import { ArrowDownIcon, ArrowUpDownIcon, SparklesIcon, RefreshCwIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import type { Bi } from '../../data/sectors';
import { overviewMap as c, LOGO_BCITI, LOGO_ATNA } from '../../data/platformOverview';
import { ENTRY_ICONS, CORE_ICONS, DEST_ICONS } from './mapParts';
function Label({
  children
}: {
  children: React.ReactNode;
}) {
  return <p className="text-center font-sans text-[11px] font-semibold tracking-[0.22em] uppercase text-[#121A3A]" data-id="element-5511" data-mp="zmwwspetar7v">{children}</p>;
}
function Connector({
  both
}: {
  both?: boolean;
}) {
  const Icon = both ? ArrowUpDownIcon : ArrowDownIcon;
  return <div className="flex flex-col items-center py-4" aria-hidden="true" data-id="element-5512" data-mp="19cq6fkve8w9h">
      <span className="w-px h-5 bg-[#CFCBF5]" data-id="element-5513" data-mp="1e9q4p9m04uq1" />
      <Icon className="w-3.5 h-3.5 text-[#8B85F0]" data-id="element-5514" data-mp="1ja4eaw394cq6" />
    </div>;
}

// Vertical version of the same architecture for small screens.
export function EcosystemMapMobile() {
  const {
    lang
  } = useLanguage();
  const pick = (b: Bi) => lang === 'FR' ? b.fr : b.en;
  return <div className="max-w-[520px] mx-auto" data-id="element-5515" data-mp="38444ioby8q6c">
      <Label data-id="element-5516" data-mp="3336nwg6l846y">{pick(c.entryLabel)}</Label>
      <p className="mt-1 text-center font-sans text-[13px] text-[#7A8096]" data-id="element-5517" data-mp="33250dc8dxp8c">{pick(c.entrySub)}</p>
      <ul className="mt-5 grid grid-cols-3 gap-2" data-id="element-5518" data-mp="2y5541t1c80uf">
        {c.entries.map(e => {
        const Icon = ENTRY_ICONS[e.key];
        return <li key={e.key} className="flex flex-col items-center text-center" data-id="element-5519" data-mp="3qt0zkuf67mpm">
              <span className="w-10 h-10 rounded-xl bg-white border border-[#E7E6F3] flex items-center justify-center" data-id="element-5520" data-mp="3qv4ysz24jxjk">
                <Icon className="w-[18px] h-[18px] text-[#4F46E5]" strokeWidth={1.6} data-id="element-5521" data-mp="3h37oduixx9lw" />
              </span>
              <span className="mt-1.5 font-sans text-[12.5px] font-medium leading-tight text-[#121A3A]" data-id="element-5522" data-mp="3c4xt7jx7u90a">{pick(e.label)}</span>
            </li>;
      })}
      </ul>

      <Connector data-id="element-5523" data-mp="dg8m7p9sjq6o" />

      <div className="rounded-[28px] bg-[#F4F3FE] p-5" data-id="element-5524" data-mp="8nxmamf1w4e4">
        <div className="flex flex-col items-center" data-id="element-5525" data-mp="8mcv96ry33l0">
          <SparklesIcon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.6} data-id="element-5526" data-mp="22v9lu6b06dub" />
          <div className="mt-1 flex items-center" aria-label="BCITI | ATNA" data-id="element-5527" data-mp="27uctzzma506i">
            <img src={LOGO_BCITI} alt="BCITI" className="h-10 w-auto object-contain" data-id="element-5528" data-mp="27wfhabw1sf7j" />
            <span className="mx-1 text-[#121A3A]/25 text-lg font-light select-none" aria-hidden="true" data-id="element-5529" data-mp="1t5ge5gdzeivc">|</span>
            <img src={LOGO_ATNA} alt="ATNA" className="h-9 w-auto object-contain" data-id="element-5530" data-mp="1y2gi9lr9tbjt" />
          </div>
          <p className="mt-1 font-sans text-[11px] font-semibold tracking-[0.22em] uppercase text-[#4F46E5] text-center" data-id="element-5531" data-mp="1k7efh7hq02b8">{pick(c.coreLabel)}</p>
        </div>
        <ul className="mt-5 flex flex-wrap justify-center gap-2" data-id="element-5532" data-mp="1a7vv4pxweein">
          {c.core.map(n => {
          const Icon = CORE_ICONS[n.key];
          return <li key={n.key} className="flex items-center gap-1.5 rounded-full bg-white border border-[#D8D5F7] pl-1 pr-3 py-1" data-id="element-5533" data-mp="34l5147r8jhw5">
                <span className="w-6 h-6 rounded-full bg-[#F1F0FD] flex items-center justify-center" data-id="element-5534" data-mp="39iwyviek9y89">
                  <Icon className="w-3.5 h-3.5 text-[#4F46E5]" strokeWidth={1.8} data-id="element-5535" data-mp="xxedi3zst0i0" />
                </span>
                <span className="font-sans text-[13px] font-semibold text-[#121A3A]" data-id="element-5536" data-mp="syutwa8og0st">{pick(n.label)}</span>
              </li>;
        })}
        </ul>
      </div>

      <Connector both data-id="element-5537" data-mp="12vfvsmpegvsf" />

      <Label data-id="element-5538" data-mp="12uxuhbnvrrdn">{pick(c.servicesLabel)}</Label>
      <ul className="mt-3 flex flex-wrap justify-center gap-2" data-id="element-5539" data-mp="1gn47wpznfk9e">
        {c.services.map(s => <li key={s.fr} className="flex items-center gap-1.5 rounded-full bg-white border border-[#E4E2F6] px-3 py-1.5" data-id="element-5540" data-mp="1bo1btv1ncodk">
            <span className="w-1.5 h-1.5 rounded-full bg-[#4F46E5]" data-id="element-5541" data-mp="1qlto8ebwcwnj" />
            <span className="font-sans text-[13px] font-medium text-[#2A3150]" data-id="element-5542" data-mp="1qixqaq01u7z3">{pick(s)}</span>
          </li>)}
      </ul>

      <div className="mt-7" data-id="element-5543" data-mp="3sbveu9msmee8">
        <Label data-id="element-5544" data-mp="3sd6ub6plpal6">{pick(c.ecosystemLabel)}</Label>
        <ul className="mt-3 flex flex-wrap justify-center gap-2" data-id="element-5545" data-mp="3ndk8i8vpim0r">
          {c.ecosystem.map(s => <li key={s.fr} className="flex items-center gap-1.5 rounded-full bg-[#FBFBFD] border border-[#ECEDF2] px-3 py-1.5" data-id="element-5546" data-mp="2egvpb32ayzse">
              <span className="w-1.5 h-1.5 rounded-full border border-[#9AA0B4]" data-id="element-5547" data-mp="2jenj62micvz8" />
              <span className="font-sans text-[13px] text-[#5A6178]" data-id="element-5548" data-mp="2oe9si0z7ts2r">{pick(s)}</span>
            </li>)}
        </ul>
        <p className="mt-4 text-center font-serif text-[16px] leading-snug text-[#4A5268]" data-id="element-5549" data-mp="2sf3l37nlrv1z">
          {c.ecosystemNote.map(l => <span key={l.fr} className="block" data-id="element-5550" data-mp="1vob0pxtmzgue">{pick(l)}</span>)}
        </p>
      </div>

      <Connector data-id="element-5551" data-mp="20muom3rsj2pe" />

      <Label data-id="element-5552" data-mp="24r2fo7lq0uqs">{pick(c.destinationsLabel)}</Label>
      <ul className="mt-3 grid grid-cols-3 gap-2" data-id="element-5553" data-mp="24p97gnvgm2go">
        {c.destinations.map(d => {
        const Icon = DEST_ICONS[d.key];
        return <li key={d.key} className="flex flex-col items-center text-center rounded-2xl bg-white border border-[#ECEBF5] px-2 py-3" data-id="element-5554" data-mp="1czw9pajbk2tl">
              <span className="w-8 h-8 rounded-full bg-[#F1F0FD] flex items-center justify-center" data-id="element-5555" data-mp="1gzx1dwvfiail">
                <Icon className="w-4 h-4 text-[#4F46E5]" strokeWidth={1.7} data-id="element-5556" data-mp="19gfeqamw14ht" />
              </span>
              <span className="mt-2 font-sans text-[12.5px] font-semibold leading-tight text-[#121A3A]" data-id="element-5557" data-mp="14jxbxgb4bg84">{pick(d.label)}</span>
            </li>;
      })}
      </ul>

      <p className="mt-6 flex items-center justify-center gap-2 font-sans text-[13px] italic text-[#4F46E5]" data-id="element-5558" data-mp="vih2pbw1hk5h">
        <RefreshCwIcon className="w-3.5 h-3.5 shrink-0" strokeWidth={1.8} aria-hidden="true" data-id="element-5559" data-mp="qj4d4jds9yz7" />
        {pick(c.loop)}
      </p>
    </div>;
}